import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ChatState, Chat, Message } from "@/types";
import { generateId } from "@/lib/utils";

interface ChatActions {
  createChat: (title?: string) => string;
  deleteChat: (chatId: string) => void;
  setCurrentChat: (chatId: string | null) => void;
  addMessage: (chatId: string, content: string, role: "user" | "assistant") => void;
  updateChatTitle: (chatId: string, title: string) => void;
  setIsTyping: (typing: boolean) => void;
  clearChats: () => void;
}

type ChatStore = ChatState & ChatActions;

export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      chats: [],
      currentChatId: null,
      isTyping: false,

      createChat: (title = "Yeni Sohbet") => {
        const newChat: Chat = {
          id: generateId(),
          title,
          createdAt: new Date(),
          updatedAt: new Date(),
          userId: "", // Will be set when user signs in
          messages: [],
        };

        set((state) => ({
          chats: [newChat, ...state.chats],
          currentChatId: newChat.id,
        }));

        return newChat.id;
      },

      deleteChat: (chatId: string) => {
        set((state) => {
          const filteredChats = state.chats.filter((chat) => chat.id !== chatId);
          const newCurrentChatId =
            state.currentChatId === chatId
              ? filteredChats[0]?.id || null
              : state.currentChatId;

          return {
            chats: filteredChats,
            currentChatId: newCurrentChatId,
          };
        });
      },

      setCurrentChat: (chatId: string | null) => {
        set({ currentChatId: chatId });
      },

      addMessage: (chatId: string, content: string, role: "user" | "assistant") => {
        const message: Message = {
          id: generateId(),
          content,
          role,
          timestamp: new Date(),
          chatId,
        };

        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: [...chat.messages, message],
                  updatedAt: new Date(),
                  title: chat.messages.length === 0 ? content.slice(0, 50) + "..." : chat.title,
                }
              : chat
          ),
        }));
      },

      updateChatTitle: (chatId: string, title: string) => {
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId ? { ...chat, title, updatedAt: new Date() } : chat
          ),
        }));
      },

      setIsTyping: (isTyping: boolean) => {
        set({ isTyping });
      },

      clearChats: () => {
        set({ chats: [], currentChatId: null });
      },
    }),
    {
      name: "chat-storage",
    }
  )
);